import { getSupabaseClient } from '../../lib/supabase'
import { normalizeAppLocale, traceAppLocale, type AppLocale } from '../../../supabase/functions/_shared/app-locale'
import { validateDesiredOutcome, visualEstimateGenericError } from './visual-estimate-validation'
import { isVisualEstimateErrorCode, shortSupportReference, visualEstimateErrorMessage, type VisualEstimateErrorCode } from '../../../supabase/functions/_shared/visual-estimate-errors'

export type VisualEstimate = {
  id: string
  status: 'draft' | 'processing' | 'completed' | 'failed'
  scope_items: string[]
  assumptions: string[]
  price_factors: string[]
  materials_min_cents: number | null
  materials_max_cents: number | null
  labor_min_cents: number | null
  labor_max_cents: number | null
  total_min_cents: number | null
  total_max_cents: number | null
  duration_min_days: number | null
  duration_max_days: number | null
  confidence: 'low' | 'medium' | 'high' | null
  disclaimer: string | null
  error_message: string | null
  generation_locale: AppLocale
}

type EstimateInput = {
  organizationId: string
  propertyId: string
  categoryId: string
  userId: string
  desiredOutcome: string
  measurements: string
  qualityTier: 'economy' | 'standard' | 'premium'
  postalCode: string
  files: File[]
  locale: AppLocale
}

type VisualEstimateFunctionResponse = {
  success?: boolean
  estimate?: VisualEstimate
  error_code?: VisualEstimateErrorCode
  correlation_id?: string
  retryable?: boolean
}

export class VisualEstimateActionError extends Error {
  constructor(message:string,public readonly code:VisualEstimateErrorCode,public readonly correlationId=''){super(message);this.name='VisualEstimateActionError'}
}

export function buildVisualEstimateInvocation(estimateId: string, localeValue: unknown, resolvedLocale: AppLocale = 'en') {
  const locale = normalizeAppLocale(localeValue, resolvedLocale)
  return { estimateId, locale, action: 'generate' as const }
}

async function visualEstimateFunctionError(error: unknown, locale: AppLocale) {
  const context = (error as { context?: Response } | null)?.context
  const technicalMessage = error instanceof Error ? error.message : 'unknown_error'
  let errorCode: VisualEstimateErrorCode = 'UNKNOWN_ERROR'
  let correlationId = ''
  let retryable = false
  if (context && typeof context.clone === 'function') {
    const payload = await context.clone().json().catch(() => null) as VisualEstimateFunctionResponse | null
    if (isVisualEstimateErrorCode(payload?.error_code)) errorCode = payload.error_code
    if (typeof payload?.correlation_id === 'string') correlationId = payload.correlation_id
    retryable = payload?.retryable === true
  }
  if (import.meta.env.DEV) console.error('AMIGO Visual Estimate request failed.', { technicalMessage, errorCode, correlationId, retryable })
  const actionable = visualEstimateErrorMessage(errorCode, locale)
  if (!correlationId || !retryable || !['UNKNOWN_ERROR','ESTIMATE_GENERATION_FAILED','DATABASE_VALIDATION_FAILED','SERVICE_NOT_AVAILABLE'].includes(errorCode)) return new VisualEstimateActionError(actionable,errorCode,correlationId)
  const reference = shortSupportReference(correlationId)
  return new VisualEstimateActionError(locale === 'es'
    ? `No pudimos completar esta solicitud. Inténtalo nuevamente o contacta a soporte. Referencia: ${reference}`
    : `We couldn’t complete this request. Try again or contact support. Reference: ${reference}`,errorCode,correlationId)
}

export async function createRequestWithoutVisualEstimate(input:{organizationId:string;propertyId:string;categoryId:string;userId:string;title:string;description:string;city:string;region:string;postalCode:string}) {
  const {data,error}=await getSupabaseClient().from('service_requests').insert({organization_id:input.organizationId,property_id:input.propertyId,category_id:input.categoryId,created_by:input.userId,title:input.title,description:input.description,city:input.city,region:input.region,postal_code:input.postalCode,status:'published',budget_min_cents:null,budget_max_cents:null}).select('id').single()
  if(error||!data)throw new Error('Unable to publish marketplace request.')
  return data.id
}

async function normalizeProjectPhoto(file: File, index: number): Promise<File> {
  if (file.size <= 0) throw new Error(`${file.name || `Photo ${index + 1}`} is empty.`)
  if (file.size > 10 * 1024 * 1024) throw new Error(`${file.name} exceeds the 10 MB limit.`)

  let bitmap: ImageBitmap
  try {
    bitmap = await createImageBitmap(file)
  } catch {
    throw new Error(
      `${file.name} is not a valid photo. Please open it and save or export it as JPEG, PNG, or WebP, then select it again.`,
    )
  }

  try {
    const maxDimension = 2400
    const scale = Math.min(1, maxDimension / Math.max(bitmap.width, bitmap.height))
    const width = Math.max(1, Math.round(bitmap.width * scale))
    const height = Math.max(1, Math.round(bitmap.height * scale))
    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const context = canvas.getContext('2d')
    if (!context) throw new Error('Your browser could not prepare the selected photo.')
    context.drawImage(bitmap, 0, 0, width, height)

    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.9))
    if (!blob || blob.size <= 0) throw new Error('Your browser could not prepare the selected photo.')
    return new File([blob], `amigo-photo-${index + 1}.jpg`, {
      type: 'image/jpeg',
      lastModified: Date.now(),
    })
  } finally {
    bitmap.close()
  }
}

export async function listEstimateCategories() {
  const { data, error } = await getSupabaseClient()
    .from('service_categories')
    .select('id,name')
    .order('display_order')
  if (error) throw error
  return data ?? []
}

export async function createAndRunVisualEstimate(input: EstimateInput): Promise<VisualEstimate> {
  if (input.files.length < 1 || input.files.length > 10) throw new Error('Upload between 1 and 10 photos.')
  const normalizedFiles = await Promise.all(input.files.map(normalizeProjectPhoto))
  const locale = normalizeAppLocale(input.locale)
  const outcomeValidation = validateDesiredOutcome(input.desiredOutcome, locale)
  if (!outcomeValidation.valid) throw new Error(outcomeValidation.message)
  traceAppLocale('client.visual-estimate', input.locale, locale, import.meta.env.DEV)
  const supabase = getSupabaseClient()
  const { data: estimate, error: estimateError } = await supabase
    .from('marketplace_visual_estimates' as never)
    .insert({
      organization_id: input.organizationId,
      property_id: input.propertyId,
      category_id: input.categoryId,
      created_by: input.userId,
      // Preserve customer-authored text. The shared validator and database
      // constraint inspect the trimmed length without rewriting the value.
      desired_outcome: input.desiredOutcome,
      measurements: input.measurements.trim() || null,
      quality_tier: input.qualityTier,
      postal_code: input.postalCode.trim(),
      status: 'draft',
      generation_locale: locale,
    } as never)
    .select('*')
    .single() as { data: { id: string } | null; error: Error | null }
  if (estimateError || !estimate) {
    if (import.meta.env.DEV) console.error('AMIGO Visual Estimate insert failed.', {
      code: (estimateError as { code?: string } | null)?.code ?? 'missing_estimate',
    })
    throw new Error(visualEstimateGenericError(locale))
  }

  try {
    for (const [index, file] of normalizedFiles.entries()) {
      const path = `${input.organizationId}/${input.userId}/${estimate.id}/${index + 1}-${crypto.randomUUID()}.jpg`
      const upload = await supabase.storage.from('marketplace-estimate-images').upload(path, file, {
        cacheControl: '3600',
        upsert: false,
        contentType: 'image/jpeg',
      })
      if (upload.error) throw upload.error
      const { error: imageError } = await supabase
        .from('marketplace_visual_estimate_images' as never)
        .insert({
          estimate_id: estimate.id,
          organization_id: input.organizationId,
          storage_path: path,
          file_name: input.files[index]?.name || file.name,
          mime_type: 'image/jpeg',
          size_bytes: file.size,
          created_by: input.userId,
        } as never)
      if (imageError) throw imageError
    }

    const result = await supabase.functions.invoke<VisualEstimateFunctionResponse>('amigo-visual-estimate', {
      body: buildVisualEstimateInvocation(estimate.id, locale),
    })
    if (result.error) throw await visualEstimateFunctionError(result.error, locale)
    if (result.data?.success === false || result.data?.error_code) {
      const syntheticResponse = new Response(JSON.stringify(result.data), { status: 400, headers: { 'Content-Type': 'application/json' } })
      throw await visualEstimateFunctionError({ context: syntheticResponse }, locale)
    }
    if (!result.data?.estimate) {
      const { data: saved } = await supabase
        .from('marketplace_visual_estimates' as never)
        .select('*')
        .eq('id', estimate.id)
        .single() as { data: VisualEstimate | null }
      if (saved?.status === 'completed') return saved
      if (saved?.error_message) throw new Error(saved.error_message)
      throw new Error('AMIGO completed the request but the estimate could not be loaded. Please try again.')
    }
    return result.data.estimate
  } catch (error) {
    const actionableError = error instanceof VisualEstimateActionError ? error : await visualEstimateFunctionError(error, locale)
    await supabase
      .from('marketplace_visual_estimates' as never)
      .update({ status: 'failed', error_message: actionableError.message } as never)
      .eq('id', estimate.id)
    throw actionableError
  }
}
